import React, { useEffect, useState, useMemo } from "react";
import Menu from "../../panel/Menu";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";
import { auth, db } from "../../firebase";
import {
  doc, getDoc, collection, getDocs, query, where,
  Timestamp, getCountFromServer, updateDoc, addDoc, setDoc, serverTimestamp, increment, onSnapshot
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Card } from "@mui/material";
import Swal from "sweetalert2";

// --- CUSTOM COLOR PALETTE & DARK THEME ---
const COLORS = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505',
  bgDark: '#031c17',
};

const TEXT_PRIMARY = COLORS.vanilla;
const TEXT_SECONDARY = "rgba(222, 226, 177, 0.7)";
const BORDER_COLOR = COLORS.calPolyGreen;
const PANEL_BG = COLORS.darkGreen;

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
});

// Helper to dynamically load Razorpay checkout script if missing
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

function Dashboard() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dynamic Property States
  const [propertyId, setPropertyId] = useState(null);
  const [propertyStatus, setPropertyStatus] = useState(null);
  const [propertyName, setPropertyName] = useState("");
  const [hotelCode, setHotelCode] = useState("");

  // Wallet State
  const [walletBalance, setWalletBalance] = useState(0);
  // Wallet Detailed Ledger States
  const [totalWalletCredit, setTotalWalletCredit] = useState(0); // Total Recharges
  const [totalWalletDebit, setTotalWalletDebit] = useState(0);   // WhatsApp Cost (count * 1.50)
  const [whatsappCount, setWhatsappCount] = useState(0);         // Total sent messages

  // Dashboard Data States
  const [dynamicBarData, setDynamicBarData] = useState([]);
  const [dashboardView, setDashboardView] = useState("reservations");
  const [roomAvailability, setRoomAvailability] = useState([]);
  const [rawRooms, setRawRooms] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));

  // Dynamic Hotel Operations States
  const [arrivalsList, setArrivalsList] = useState([]);
  const [blockedLossList, setBlockedLossList] = useState([]);
  const [operationalTasks, setOperationalTasks] = useState([]);

  // Block Unit Form State
  const [blockReason, setBlockReason] = useState("");
  const [blockStartDate, setBlockStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [blockEndDate, setBlockEndDate] = useState(moment().add(1, "days").format("YYYY-MM-DD"));
  const [blockSelectedRoomId, setBlockSelectedRoomId] = useState("");
  const [blockAlertGM, setBlockAlertGM] = useState(false);
  const [isBlockingSubmitting, setIsBlockingSubmitting] = useState(false);

  // Performance Tab Dynamic Analytics
  const [sourceRevenueData, setSourceRevenueData] = useState([]);
  const [roomTypeRevenueData, setRoomTypeRevenueData] = useState([]);
  const [preBookingWindowDays, setPreBookingWindowDays] = useState(0);

  // Dynamic Booking States (OTA)
  const [bookings, setBookings] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(6);

  const [summary, setSummary] = useState({
    today: 0, tomorrow: 0, month: 0, future: 0,
    live: 0, available: 0, cleaning: 0, totalRooms: 0,
    checkout: 0, waiting: 0, cancelled: 0, hold: 0, noShow: 0, blocked: 0, paymentPending: 0, todayRevenue: 0,
    monthRevenue: 0, whatsappCurrentMonth: 0, whatsappPrevMonth: 0
  });

  // Auth listener
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => setUserData(user));
    return () => unsub();
  }, []);

  // Update block form date whenever selectedDate changes
  useEffect(() => {
    setBlockStartDate(selectedDate);
    setBlockEndDate(moment(selectedDate).add(1, "days").format("YYYY-MM-DD"));
  }, [selectedDate]);

  // Combined Dashboard & Property Initializer with Dynamic Wallet Ledger
  useEffect(() => {
    if (!userData) return;

    const loadDashboardData = async () => {
      try {
        const roleSnap = await getDoc(doc(db, "authroles", userData.uid));
        if (!roleSnap.exists() || !roleSnap.data().propertyId) {
          setPropertyStatus("no-property");
          setTimeout(() => setLoading(false), 500);
          return;
        }

        const activePropertyId = roleSnap.data().propertyId;
        setPropertyId(activePropertyId);

        // Concurrent fetch: Property details, Channel manager, Wallet credits, and WhatsApp usage
        const [propSnap, cmSnap, walletHistorySnap, waCountSnap] = await Promise.all([
          getDoc(doc(db, "properties", activePropertyId)),
          getDoc(doc(db, "channel_manager", activePropertyId)),
          getDocs(collection(db, "properties", activePropertyId, "wallet_history")),
          getCountFromServer(
            query(
              collection(db, "whatsapphistory"),
              where("propertyId", "==", activePropertyId)
            )
          ),
        ]);

        // --- Compute Dynamic Wallet Ledger ---
        let totalCredited = 0;
        walletHistorySnap.docs.forEach((d) => {
          const item = d.data();
          if ((item.type || "CREDIT").toUpperCase() === "CREDIT") {
            totalCredited += Number(item.amount || 0);
          }
        });

        const waMsgCount = waCountSnap.data().count || 0;
        const totalDebited = waMsgCount * 1.5; // ₹1.50 per alert
        const computedNetBalance = totalCredited - totalDebited; // Allows negative balance

        setTotalWalletCredit(totalCredited);
        setTotalWalletDebit(totalDebited);
        setWhatsappCount(waMsgCount);
        setWalletBalance(computedNetBalance);

        // --- Property & Channel Manager Setup ---
        if (propSnap.exists()) {
          const p = propSnap.data();
          setPropertyName(p.hotelname || "Premier Property");
          setPropertyStatus(p.status || "Active");

          if (p.status === "Active") {
            await fetchDashboardAnalytics(activePropertyId);
          }
        }

        if (cmSnap.exists()) {
          const cmData = cmSnap.data();
          setHotelCode(cmData.hotelCode || "");
        }
      } catch (err) {
        console.error("Dashboard Engine Load Error: ", err);
      } finally {
        setTimeout(() => setLoading(false), 600);
      }
    };

    loadDashboardData();
  }, [userData, selectedDate]);

  // Live real-time listener for Property Wallet
  // useEffect(() => {
  //   if (!propertyId) return;
  //   const unsub = onSnapshot(doc(db, "properties", propertyId), (docSnap) => {
  //     if (docSnap.exists()) {
  //       setWalletBalance(Number(docSnap.data().walletBalance || 0));
  //     }
  //   });
  //   return () => unsub();
  // }, [propertyId]);

  const fetchDashboardAnalytics = async (targetPropertyId) => {
    try {
      const targetDateMoment = moment(selectedDate);
      const targetDateStr = targetDateMoment.format("YYYY-MM-DD");
      const tomorrowStr = targetDateMoment.clone().add(1, "day").format("YYYY-MM-DD");
      const monthStartStr = targetDateMoment.clone().startOf("month").format("YYYY-MM-DD");
      const monthEndStr = targetDateMoment.clone().endOf("month").format("YYYY-MM-DD");

      const currentMonthStart = Timestamp.fromDate(targetDateMoment.clone().startOf("month").toDate());
      const currentMonthEnd = Timestamp.fromDate(targetDateMoment.clone().endOf("month").toDate());
      const prevMonthStart = Timestamp.fromDate(targetDateMoment.clone().subtract(1, "month").startOf("month").toDate());
      const prevMonthEnd = Timestamp.fromDate(targetDateMoment.clone().subtract(1, "month").endOf("month").toDate());

      const waCurrentQuery = query(
        collection(db, "whatsapphistory"),
        where("propertyId", "==", targetPropertyId),
        where("createdAt", ">=", currentMonthStart),
        where("createdAt", "<=", currentMonthEnd)
      );
      const waPrevQuery = query(
        collection(db, "whatsapphistory"),
        where("propertyId", "==", targetPropertyId),
        where("createdAt", ">=", prevMonthStart),
        where("createdAt", "<=", prevMonthEnd)
      );

      const [
        onlinePaidTodaySnap, onlinePaidTomorrowSnap, onlinePaidFutureSnap,
        allMonthResSnap,
        roomsSnap,
        checkinDetailsDateSnap,
        waCurrentSnap, waPrevSnap,
        tasksSnap
      ] = await Promise.all([
        getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", "==", targetDateStr))),
        getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", "==", tomorrowStr))),
        getDocs(query(collection(db, "onlinebookings"), where("propertyId", "==", targetPropertyId), where("status", "==", "Paid"), where("checkindate", ">", tomorrowStr))),
        getDocs(query(collection(db, "reservations"), where("propertyId", "==", targetPropertyId), where("checkindate", "<=", monthEndStr))),
        getDocs(query(collection(db, "roominfos"), where("propertyId", "==", targetPropertyId))),
        getDocs(query(collection(db, "checkindetails"), where("propertyId", "==", targetPropertyId), where("checkindate", "<=", targetDateStr), where("checkoutdate", ">", targetDateStr))),
        getCountFromServer(waCurrentQuery),
        getCountFromServer(waPrevQuery),
        getDocs(query(collection(db, "operational_tasks"), where("propertyId", "==", targetPropertyId)))
      ]);

      const rooms = roomsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRawRooms(rooms);

      const monthReservationsDocs = [];
      const arrivalsOnDate = [];
      const checkoutsOnDate = [];
      const activeStayOnDateReservations = [];
      const blockedResOnDate = [];
      let cancelledCount = 0;
      let holdCount = 0;
      let noShowCount = 0;
      let pendingCount = 0;

      allMonthResSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const chkIn = data.checkindate;
        const chkOut = data.checkoutdate || chkIn;
        const status = (data.status || "").toLowerCase();

        if (chkOut >= monthStartStr && chkIn <= monthEndStr) {
          monthReservationsDocs.push(docSnap);
        }

        if (chkIn === targetDateStr && status !== "cancelled") {
          arrivalsOnDate.push(docSnap);
        }
        if (chkOut === targetDateStr && status !== "cancelled") {
          checkoutsOnDate.push(docSnap);
        }

        const isActiveOnDate = (chkIn <= targetDateStr && chkOut > targetDateStr) || (chkIn === targetDateStr && chkOut === targetDateStr);
        if (isActiveOnDate) {
          if (status === "booked" || status === "occupied" || status === "live") {
            activeStayOnDateReservations.push(docSnap);
          } else if (status === "blocked") {
            blockedResOnDate.push(docSnap);
          } else if (status === "hold") {
            holdCount++;
          } else if (status === "pending") {
            pendingCount++;
          }
        }

        if (status === "cancelled" && chkIn >= monthStartStr && chkIn <= monthEndStr) {
          cancelledCount++;
        }
        if (status === "no show" && chkIn === targetDateStr) {
          noShowCount++;
        }
      });

      const occupiedRoomIdSet = new Set();
      const blockedRoomIdSet = new Set();

      checkinDetailsDateSnap.docs.forEach(d => {
        const item = d.data();
        const st = (item.status || "").toLowerCase();
        if (st === "blocked") {
          if (item.roomid) blockedRoomIdSet.add(item.roomid);
        } else if (st !== "cancelled") {
          if (item.roomid) occupiedRoomIdSet.add(item.roomid);
        }
      });

      activeStayOnDateReservations.forEach(d => {
        const res = d.data();
        res.rooms?.forEach(r => {
          if (r.roomid) occupiedRoomIdSet.add(r.roomid);
        });
      });
      blockedResOnDate.forEach(d => {
        const res = d.data();
        res.rooms?.forEach(r => {
          if (r.roomid) blockedRoomIdSet.add(r.roomid);
        });
      });

      const groupedRooms = {};
      let totalAvailableCount = 0;
      let totalOccupiedCount = 0;
      let totalBlockedCount = 0;
      let totalCleaningCount = 0;

      rooms.forEach((room) => {
        const category = room.roomTypeName || room.roomtype || room.category || "Standard";
        const price = Number(room.price || room.roomrent || room.amount || 0);

        if (!groupedRooms[category]) {
          groupedRooms[category] = { category, available: 0, total: 0, price };
        }
        groupedRooms[category].total += 1;

        const isBlocked = blockedRoomIdSet.has(room.id);
        const isOccupied = occupiedRoomIdSet.has(room.id);
        const isUnderCleaning = room.status === "Cleaning";

        if (isBlocked) {
          totalBlockedCount++;
        } else if (isOccupied) {
          totalOccupiedCount++;
        } else if (isUnderCleaning && targetDateStr === moment().format("YYYY-MM-DD")) {
          totalCleaningCount++;
        } else {
          groupedRooms[category].available += 1;
          totalAvailableCount++;
        }
      });

      setRoomAvailability(Object.values(groupedRooms));

      let calcDateRevenue = onlinePaidTodaySnap.docs.reduce((sum, d) => sum + Number(d.data().totalamount || 0), 0);
      arrivalsOnDate.forEach(d => {
        calcDateRevenue += Number(d.data().totalamount || d.data().rooamount || 0);
      });

      let calcMonthRevenue = 0;
      monthReservationsDocs.forEach(d => {
        const amt = Number(d.data().totalamount || d.data().rooamount || 0);
        calcMonthRevenue += amt;
      });

      setSummary({
        today: arrivalsOnDate.length + onlinePaidTodaySnap.size,
        tomorrow: onlinePaidTomorrowSnap.size,
        month: monthReservationsDocs.length,
        future: onlinePaidFutureSnap.size,
        live: totalOccupiedCount,
        available: totalAvailableCount,
        cleaning: totalCleaningCount,
        totalRooms: roomsSnap.size,
        checkout: checkoutsOnDate.length,
        waiting: pendingCount,
        cancelled: cancelledCount,
        hold: holdCount,
        noShow: noShowCount,
        blocked: totalBlockedCount,
        paymentPending: pendingCount,
        todayRevenue: calcDateRevenue.toFixed(0),
        monthRevenue: calcMonthRevenue,
        whatsappCurrentMonth: waCurrentSnap.data().count,
        whatsappPrevMonth: waPrevSnap.data().count
      });

      const dynamicArrivals = arrivalsOnDate.map(d => {
        const data = d.data();
        const roomNumbersStr = data.rooms?.map(r => r.roomno).join(", ") || "TBD";
        return {
          guest: data.customers?.custname || "Guest",
          room: roomNumbersStr,
          time: data.checkintime || "12:00 PM",
          source: data.bookingsource || "Direct"
        };
      });
      setArrivalsList(dynamicArrivals);

      const dynamicLoss = [];
      blockedResOnDate.forEach(d => {
        const data = d.data();
        const days = Number(data.noofdays || moment(data.checkoutdate).diff(moment(data.checkindate), "days") || 1);
        data.rooms?.forEach(r => {
          const roomObj = rooms.find(rm => rm.id === r.roomid || rm.roomNo === r.roomno);
          const rate = Number(r.roomrate || roomObj?.price || roomObj?.roomrent || 2000);
          const lossVal = rate * days;
          dynamicLoss.push({
            room: r.roomno || "N/A",
            reason: data.note || "Maintenance",
            days: days,
            loss: lossVal.toLocaleString("en-IN")
          });
        });
      });
      setBlockedLossList(dynamicLoss);

      if (!tasksSnap.empty) {
        setOperationalTasks(tasksSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        setOperationalTasks([
          { id: "task-1", concern: "Bathroom Leakage", room: "205", solved: false },
          { id: "task-2", concern: "TV Signal Check", room: "301", solved: true }
        ]);
      }

      const sourceRevMap = {};
      const roomTypeRevMap = {};
      let totalLeadDays = 0;
      let reservationCountWithLead = 0;

      monthReservationsDocs.forEach(docu => {
        const rData = docu.data();
        const src = rData.bookingsource || "Direct";
        const amt = Number(rData.totalamount || rData.rooamount || 0);

        if (!sourceRevMap[src]) sourceRevMap[src] = { source: src, revenue: 0, rooms: 0 };
        sourceRevMap[src].revenue += amt;
        sourceRevMap[src].rooms += Number(rData.noofrooms || rData.rooms?.length || 1);

        rData.rooms?.forEach(rm => {
          const tName = rm.roomtypename || "Standard Room";
          if (!roomTypeRevMap[tName]) roomTypeRevMap[tName] = { room: tName, roomNos: new Set(), revenue: 0 };
          roomTypeRevMap[tName].revenue += Number(rm.roomrate || (amt / (rData.rooms.length || 1)));
          if (rm.roomno) roomTypeRevMap[tName].roomNos.add(rm.roomno);
        });

        if (rData.createdAt && rData.checkindate) {
          const createdMoment = moment(rData.createdAt.toDate ? rData.createdAt.toDate() : rData.createdAt);
          const inMoment = moment(rData.checkindate);
          const diff = inMoment.diff(createdMoment, "days");
          if (diff >= 0) {
            totalLeadDays += diff;
            reservationCountWithLead++;
          }
        }
      });

      const sourceColorPalette = [COLORS.gold, COLORS.calPolyGreen, COLORS.vanilla, COLORS.goldenrod];
      const computedSourceRev = Object.values(sourceRevMap).map((item, idx) => ({
        ...item,
        color: sourceColorPalette[idx % sourceColorPalette.length]
      }));
      setSourceRevenueData(computedSourceRev);

      const computedRoomTypeRev = Object.values(roomTypeRevMap).map(item => ({
        room: item.room,
        roomNos: Array.from(item.roomNos).join(",") || "Assigned",
        revenue: item.revenue
      }));
      setRoomTypeRevenueData(computedRoomTypeRev);

      setPreBookingWindowDays(reservationCountWithLead > 0 ? Math.round(totalLeadDays / reservationCountWithLead) : 4);

      const barData = Object.keys(sourceRevMap).map(k => ({
        name: k,
        rooms: sourceRevMap[k].rooms
      }));
      setDynamicBarData(barData.length > 0 ? barData : [
        { name: "Booking.com", rooms: 14 },
        { name: "Website", rooms: 9 },
        { name: "Walk-In", rooms: 5 },
        { name: "Agoda", rooms: 6 },
        { name: "Expedia", rooms: 4 }
      ]);

    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  // --- DYNAMIC WALLET PAYMENT & TOP-UP VIA RAZORPAY ---
  const handleOpenAddMoneyModal = async () => {
    if (!propertyId) {
      return Toast.fire({ icon: "error", title: "No active property linked." });
    }

    const { value: amount } = await Swal.fire({
      title: `<span style="color:${COLORS.gold}; font-size:1.25rem;">Recharge Hotel Wallet</span>`,
      text: "Enter amount to add to your hotel wallet balance:",
      input: "number",
      inputValue: "1000",
      background: COLORS.darkGreen,
      color: COLORS.vanilla,
      showCancelButton: true,
      confirmButtonColor: COLORS.goldenrod,
      cancelButtonColor: COLORS.bgDark,
      confirmButtonText: "Proceed to Pay",
      inputAttributes: {
        min: "100",
        step: "100"
      },
      inputValidator: (val) => {
        if (!val || Number(val) < 100) {
          return "Please enter a valid amount (minimum ₹100)";
        }
      }
    });

    if (amount) {
      executeWalletRecharge(Number(amount));
    }
  };

  const executeWalletRecharge = async (topUpAmount) => {
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      return Toast.fire({ icon: "error", title: "Payment gateway failed to load. Please check your internet." });
    }

    const userEmail = userData?.email || "hotelier@hotelzio.com";
    const userPhone = userData?.phoneNumber || "9999999999";
    const userName = userData?.displayName || propertyName || "Hotel Owner";

    const options = {
      key: "rzp_live_TIpdM6KeXVoeFf",
      amount: topUpAmount * 100, // in paise
      currency: "INR",
      name: "HOTELZIO",
      description: `Wallet Top-Up (₹${topUpAmount})`,
      notes: {
        propertyId,
        purpose: "Hotel Wallet Recharge",
        userId: userData?.uid || "unknown"
      },
      prefill: {
        name: userName,
        email: userEmail,
        contact: userPhone
      },
      theme: { color: COLORS.darkGreen },
      handler: async function (response) {
        try {
          Toast.fire({ icon: "info", title: "Verifying and updating wallet balance..." });

          // 1. Record transaction in wallet history subcollection
          await addDoc(collection(db, "properties", propertyId, "wallet_history"), {
            amount: topUpAmount,
            type: "CREDIT",
            status: "Success",
            description: "Wallet recharge via Razorpay",
            paymentId: response.razorpay_payment_id,
            propertyId: propertyId,
            createdAt: serverTimestamp(),
            timestampStr: moment().format("YYYY-MM-DD HH:mm:ss")
          });

          // 2. Increment property wallet balance
          await updateDoc(doc(db, "properties", propertyId), {
            walletBalance: increment(topUpAmount),
            lastWalletUpdate: serverTimestamp()
          });

          Swal.fire({
            icon: "success",
            title: "Wallet Recharged!",
            html: `Added <strong style="color:${COLORS.gold}">₹${topUpAmount.toLocaleString("en-IN")}</strong> to your hotel balance.<br/><small style="opacity:0.7">Ref: ${response.razorpay_payment_id}</small>`,
            background: COLORS.darkGreen,
            color: COLORS.vanilla,
            confirmButtonColor: COLORS.goldenrod
          });

        } catch (error) {
          console.error("Wallet update error:", error);
          Toast.fire({ icon: "error", title: "Payment completed but balance sync failed." });
        }
      },
      modal: {
        ondismiss: function () {
          Toast.fire({ icon: "warning", title: "Payment cancelled." });
        }
      }
    };

    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.open();
  };

  const handleBlockRoomSubmit = async () => {
    if (!blockSelectedRoomId) {
      return Toast.fire({ icon: "error", title: "Please select a room to block." });
    }
    if (!blockStartDate || !blockEndDate) {
      return Toast.fire({ icon: "error", title: "Please specify valid block dates." });
    }

    try {
      setIsBlockingSubmitting(true);
      const selectedRoom = rawRooms.find(r => r.id === blockSelectedRoomId);
      const days = moment(blockEndDate).diff(moment(blockStartDate), "days") || 1;

      const q = query(collection(db, "reservations"), where("propertyId", "==", propertyId));
      const bookingCountSnap = await getCountFromServer(q);
      const invoiceNo = bookingCountSnap.data().count + 1;

      const blockPayload = {
        invoiceno: invoiceNo,
        rooms: [{
          roomid: selectedRoom.id,
          roomno: selectedRoom.roomNo,
          rtypeid: selectedRoom.roomTypeId || "",
          roomtypename: selectedRoom.roomTypeName || selectedRoom.category || "Standard",
          roomrate: selectedRoom.price || selectedRoom.roomrent || 0,
        }],
        checkindate: blockStartDate,
        checkoutdate: blockEndDate,
        noofdays: days,
        noofrooms: 1,
        note: blockReason || "Room blocked for maintenance",
        bookingDate: moment().format("YYYY-MM-DD"),
        type: "Block",
        status: "Blocked",
        alertGM: blockAlertGM,
        propertyId,
        uid: auth.currentUser?.uid || "system",
        createdAt: serverTimestamp()
      };

      const blockResRef = await addDoc(collection(db, "reservations"), blockPayload);

      for (let day = 0; day < days; day++) {
        const inDate = moment(blockStartDate).add(day, "days").format("YYYY-MM-DD");
        const outDate = moment(blockStartDate).add(day + 1, "days").format("YYYY-MM-DD");
        await addDoc(collection(db, "checkindetails"), {
          bookingid: blockResRef.id,
          checkindate: inDate,
          checkoutdate: outDate,
          propertyId,
          roomid: selectedRoom.id,
          roomname: selectedRoom.roomNo,
          status: "Blocked",
          createdAt: Timestamp.now()
        });
      }

      await updateDoc(doc(db, "roominfos", selectedRoom.id), { status: "Blocked" });

      if (blockReason) {
        await addDoc(collection(db, "operational_tasks"), {
          propertyId,
          concern: blockReason,
          room: selectedRoom.roomNo,
          solved: false,
          createdAt: serverTimestamp()
        });
      }

      Toast.fire({ icon: "success", title: `Room ${selectedRoom.roomNo} successfully blocked.` });

      setBlockReason("");
      setBlockSelectedRoomId("");
      setBlockAlertGM(false);
      await fetchDashboardAnalytics(propertyId);

    } catch (err) {
      console.error("Room Block Submission Error:", err);
      Toast.fire({ icon: "error", title: "Failed to block room.", text: err.message });
    } finally {
      setIsBlockingSubmitting(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const newStatus = !task.solved;
      if (task.id && !task.id.startsWith("task-")) {
        await updateDoc(doc(db, "operational_tasks", task.id), { solved: newStatus });
      }
      setOperationalTasks(prev => prev.map(t => t.id === task.id ? { ...t, solved: newStatus } : t));
      Toast.fire({ icon: "success", title: `Task marked as ${newStatus ? "Done" : "Pending"}` });
    } catch (err) {
      console.error("Task update error:", err);
    }
  };

  useEffect(() => {
    if (!hotelCode || !propertyId) return;

    const fetchOtaBookings = async () => {
      try {
        const transferredIds = new Set();
        const logsSnap = await getDocs(collection(db, "properties", propertyId, "ota_raw_logs"));
        logsSnap.forEach(d => {
          if (d.data().syncStatus === "transferred") transferredIds.add(String(d.id));
        });

        const response = await fetch('https://booking.thekingsexecutive.com/api/webhooks-data');
        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          const uniqueBookingsMap = new Map();

          result.data.forEach(item => {
            let parsedInnerData = {};
            try { parsedInnerData = typeof item.data === 'string' ? JSON.parse(item.data) : item.data; } catch (e) { }

            const resReservation = parsedInnerData?.HotelReservations?.HotelReservation;
            const otaId = resReservation?.UniqueID?.ID;
            const checkInDate = resReservation?.RoomStays?.[0]?.TimeSpan?.Start;
            const checkOutDate = resReservation?.RoomStays?.[0]?.TimeSpan?.End || checkInDate;
            const incomingHotelCode = resReservation?.RoomStays?.[0]?.BasicPropertyInfo?.HotelCode;

            if (incomingHotelCode && String(incomingHotelCode).trim() === String(hotelCode).trim()) {
              if (checkOutDate && checkOutDate >= selectedDate) {
                const isTransferred = otaId ? transferredIds.has(String(otaId)) : false;
                const formattedItem = { id: item.id, createdAt: item.created_at, payload: parsedInnerData, isTransferred };

                if (otaId) {
                  if (!uniqueBookingsMap.has(otaId) || uniqueBookingsMap.get(otaId).id < item.id) {
                    uniqueBookingsMap.set(otaId, formattedItem);
                  }
                } else {
                  uniqueBookingsMap.set(`fallback-${item.id}`, formattedItem);
                }
              }
            }
          });

          const finalUniqueArray = Array.from(uniqueBookingsMap.values()).sort((a, b) => b.id - a.id);
          setBookings(finalUniqueArray);

          if (finalUniqueArray.length > 0) autoSyncBookings(finalUniqueArray);
        }
      } catch (error) { console.error("OTA Fetch error:", error); }
    };
    fetchOtaBookings();
  }, [hotelCode, propertyId, selectedDate]);

  const autoSyncBookings = async (incomingBookings) => {
    if (!propertyId || incomingBookings.length === 0) return;
    try {
      let syncOccurred = false;
      for (const row of incomingBookings) {
        if (!row.isTransferred) {
          await processReservationTransfer(row, true);
          syncOccurred = true;
        }
      }
      if (syncOccurred) fetchDashboardAnalytics(propertyId);
    } catch (err) { console.error("Log auto-sync error:", err); }
  };

  const processReservationTransfer = async (booking, isAuto = false) => {
    if (!propertyId) return;
    try {
      if (!isAuto) Toast.fire({ icon: "info", title: "Processing reservation transfer..." });

      const resReservation = booking.payload?.HotelReservations?.HotelReservation;
      const otaId = resReservation?.UniqueID?.ID || `ota-${booking.id}`;
      const otaLogRef = doc(db, "properties", propertyId, "ota_raw_logs", String(otaId));
      const logSnap = await getDoc(otaLogRef);

      if (logSnap.exists() && logSnap.data().syncStatus === "transferred") {
        if (!isAuto) Toast.fire({ icon: "warning", title: "Already transferred!" });
        return;
      }

      const customer = resReservation?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
      const roomStay = resReservation?.RoomStays?.[0];
      const channelName = booking.payload?.POS?.Source?.BookingChannel?.CompanyName || "OTA Channel";

      const custname = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim() || "Guest";
      const email = customer?.Email || "N/A";
      const mobile = customer?.Telephone?.[0]?.PhoneNumber || "0000000000";

      const checkindate = roomStay?.TimeSpan?.Start || moment().format("YYYY-MM-DD");
      const checkoutdate = roomStay?.TimeSpan?.End || moment().add(1, "days").format("YYYY-MM-DD");
      const totalamount = Number(roomStay?.Total?.AmountAfterTax || roomStay?.Total?.AmountBeforeTax || 0);

      const inMoment = moment(checkindate);
      const outMoment = moment(checkoutdate);
      const noofdays = outMoment.diff(inMoment, 'days') > 0 ? outMoment.diff(inMoment, 'days') : 1;
      const roomRate = totalamount / noofdays;
      const roomNameOTA = roomStay?.RoomTypes?.RoomType?.RoomDescription?.Name || "Unassigned OTA Room";

      const qRooms = query(collection(db, "roominfos"), where("propertyId", "==", propertyId), where("status", "==", "Available"));
      const availableRoomsSnap = await getDocs(qRooms);
      let assignedRoomData = null;

      if (!availableRoomsSnap.empty) {
        assignedRoomData = { id: availableRoomsSnap.docs[0].id, ...availableRoomsSnap.docs[0].data() };
        await updateDoc(doc(db, "roominfos", assignedRoomData.id), { status: "Occupied" });
      }

      const bookingrooms = [{
        roomid: assignedRoomData ? assignedRoomData.id : "ota_pending_room",
        rtypeid: assignedRoomData ? assignedRoomData.roomTypeId : "ota_type",
        planid: "ota_plan",
        roomno: assignedRoomData ? assignedRoomData.roomNo : roomNameOTA,
        planname: "EP",
        roomtypename: assignedRoomData ? assignedRoomData.roomTypeName : "OTA Category",
        roomrate: roomRate, discount: 0, amount: totalamount
      }];

      const customerData = { custname, mobile, email, address: "", city: "", state: "", gender: "", gstname: "", gstno: "", documenttype: "", otherDocumentType: "", idno: "", propertyId, uid: auth.currentUser?.uid || "system", createdAt: Timestamp.now() };
      const customerRef = await addDoc(collection(db, "customers"), customerData);

      const q = query(collection(db, "reservations"), where("propertyId", "==", propertyId));
      const bookingCountSnap = await getCountFromServer(q);
      const invoiceNo = bookingCountSnap.data().count + 1;

      const bookingPayload = {
        customerId: customerRef.id, invoiceno: invoiceNo, customers: customerData, rooms: bookingrooms, services: [],
        checkindate, checkoutdate, noofdays, checkintime: "12:00", checkouttime: "11:00", adults: 2, childs: 0, noofrooms: 1,
        visitpurpose: "Leisure", bookingsource: channelName, otaId: otaId,
        visitorPhotoUrl: "", idFrontUrl: "", idBackUrl: "", couplePhotoUrl: "",
        rooamount: totalamount, roundoff: 0, totalamount: totalamount, advance: totalamount, remaining: 0,
        note: `Transferred from ${channelName} (ID: ${otaId})`, bookingDate: moment().format("YYYY-MM-DD"), type: "Regular", status: "Booked",
        approval1: "No", approval2: "No", approval3: "No", approval4: "No", damageamount: 0, propertyId, uid: auth.currentUser?.uid || "system", additionalAdults: [], createdAt: Timestamp.now(),
      };
      const bookingRef = await addDoc(collection(db, "reservations"), bookingPayload);

      for (let day = 0; day < noofdays; day++) {
        const inDate = moment(checkindate).add(day, "days").format("YYYY-MM-DD");
        const outDate = moment(checkindate).add(day + 1, "days").format("YYYY-MM-DD");
        for (const room of bookingrooms) {
          await addDoc(collection(db, "checkindetails"), {
            bookingid: bookingRef.id, customerId: customerRef.id, checkindate: inDate, checkoutdate: outDate,
            propertyId, roomid: room.roomid, roomtypeid: room.rtypeid, planid: room.planid, roomname: room.roomno,
            planname: room.planname, roomtype: room.roomtypename, status: "Booked", uid: auth.currentUser?.uid || "system", createdAt: Timestamp.now(),
          });
        }
      }

      if (totalamount > 0) {
        for (let i = 0; i < noofdays; i++) {
          await addDoc(collection(db, "payhistory"), {
            bookingid: bookingRef.id, customerId: customerRef.id, paymode1: "", amount1: totalamount / noofdays, paytime1: moment().format("HH:mm"),
            paymode2: "", amount2: 0, paytime2: "", type: "Booking", paymenttype: "B", status: "Working", propertyId, uid: auth.currentUser?.uid || "system",
            note: `${channelName} Pre-paid`, roomRate: roomRate, totalAmount: totalamount, createdAt: Timestamp.fromDate(new Date(new Date().setDate(new Date().getDate() + i))),
          });
        }
      }

      await setDoc(otaLogRef, { otaLogId: booking.id, createdAt: booking.createdAt || new Date().toISOString(), syncStatus: "transferred", localBookingId: bookingRef.id, payload: booking.payload }, { merge: true });

      setBookings(prev => prev.map(b => {
        const bOtaId = b.payload?.HotelReservations?.HotelReservation?.UniqueID?.ID || `ota-${b.id}`;
        if (String(bOtaId) === String(otaId)) return { ...b, isTransferred: true };
        return b;
      }));

      if (!isAuto) Toast.fire({ icon: "success", title: "Reservation successfully transferred!" });
      if (!isAuto) fetchDashboardAnalytics(propertyId);

    } catch (error) {
      console.error("Transfer Error:", error);
      if (!isAuto) Toast.fire({ icon: "error", title: "Transfer failed", text: error.message });
    }
  };

  const handleViewDetails = (booking) => {
    const resDetails = booking.payload?.HotelReservations?.HotelReservation;
    const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
    const roomStay = resDetails?.RoomStays?.[0];

    const email = customer?.Email || "N/A";
    const commentText = resDetails?.ResGlobalInfo?.Comments?.Comment?.Text || roomStay?.Comments?.Comment?.Text || "None";
    const payoutType = roomStay?.DepositPayments?.GuaranteePayment?.Description?.Text || "N/A";
    const guestCounts = roomStay?.GuestCounts || [];
    const structuredGuests = guestCounts.map(g => `${g.AgeQualifyingCode === "10" ? "Adults" : "Children"}: ${g.Count}`).join(', ') || "N/A";

    Swal.fire({
      title: `<strong style="color:${COLORS.gold}; font-size:1.1rem;">Booking Details (ID: ${resDetails?.UniqueID?.ID || booking.id})</strong>`,
      background: COLORS.darkGreen,
      color: COLORS.vanilla,
      html: `
        <div style="text-align: left; font-size: 0.85rem; max-height: 480px; overflow-y: auto; font-family: sans-serif; color: ${COLORS.vanilla}; opacity: 0.9;">
            <div style="margin-bottom: 12px; border-bottom: 1px solid ${COLORS.calPolyGreen}; padding-bottom: 8px;">
                <p style="margin: 4px 0;"><strong style="color:${COLORS.gold}">Guest Email:</strong> ${email}</p>
                <p style="margin: 4px 0;"><strong style="color:${COLORS.gold}">Occupancy Breakdown:</strong> ${structuredGuests}</p>
                <p style="margin: 4px 0;"><strong style="color:${COLORS.gold}">Payment Basis:</strong> ${payoutType}</p>
            </div>
            <p style="margin: 4px 0; font-weight: bold; color: ${COLORS.gold};">Special Requests & Rates:</p>
            <pre style="background: ${COLORS.bgDark}; color: ${COLORS.vanilla}; padding: 12px; border-radius: 6px; white-space: pre-wrap; font-family: monospace; font-size: 0.8rem; border: 1px solid ${COLORS.calPolyGreen}; margin-top: 6px;">${commentText}</pre>
        </div>
      `,
      showCloseButton: true,
      confirmButtonColor: COLORS.goldenrod,
    });
  };

  const filteredBookings = useMemo(() => {
    return bookings.filter((item) => {
      const resDetails = item.payload?.HotelReservations?.HotelReservation;
      const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
      const customerName = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim();
      const channelName = item.payload?.POS?.Source?.BookingChannel?.CompanyName || "";
      const otaId = resDetails?.UniqueID?.ID || "";
      return `${customerName} ${channelName} ${otaId}`.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [bookings, searchText]);

  const currentRows = useMemo(() => {
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    return filteredBookings.slice(indexOfFirstRow, indexOfLastRow);
  }, [filteredBookings, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredBookings.length / rowsPerPage);

  const SUMMARY_CARDS = [
    { title: "Total Rooms", value: summary.totalRooms || 0, icon: "fa-hotel", color: COLORS.gold, link: "/rooms" },
    { title: "View Bookings", value: summary.month || 0, icon: "fa-calendar-check", color: COLORS.vanilla, link: "/view-reservations" },
    { title: "Stay In", value: summary.live || 0, icon: "fa-bed", color: COLORS.goldenrod, link: "/live-reservations" },
    { title: "Checking Out", value: summary.checkout || 0, icon: "fa-door-open", color: COLORS.gold, link: "/checkouts" },
    { title: "Waiting Guest", value: summary.waiting || 0, icon: "fa-users", color: COLORS.vanilla, link: "/pending-reservations" },
    { title: "Cancellations", value: summary.cancelled || 0, icon: "fa-ban", color: COLORS.goldenrod, link: "/cancelled-bookings" },
    { title: "On Hold", value: summary.hold || 0, icon: "fa-pause-circle", color: COLORS.gold, link: "/hold-rooms" },
    { title: "No Shows", value: summary.noShow || 0, icon: "fa-user-slash", color: COLORS.vanilla, link: "/no-shows" },
    { title: "Blocked", value: summary.blocked || 0, icon: "fa-lock", color: COLORS.goldenrod, link: "/blocked-rooms" },
    { title: "Pay Links", value: summary.paymentPending || 0, icon: "fa-link", color: COLORS.gold, link: "/payment-pending" },
    { title: "Date Rev", value: `₹${summary.todayRevenue || 0}`, icon: "fa-indian-rupee-sign", color: COLORS.vanilla, link: "/today-revenue" }
  ];

  if (loading) return <div className="p-5 text-center" style={{ background: COLORS.bgDark, color: COLORS.vanilla, height: '100vh' }}><h5>Loading Professional Dashboard...</h5></div>;

  return (
    <div>
      <div
        style={{
          padding: 10,
          background: COLORS.bgDark,
          marginBottom: 20,
          minHeight: "100vh",
          overflowX: "auto",
          overflowY: "auto",
          paddingBottom: "120px",
          scrollBehavior: "smooth",
          scrollbarWidth: "thin"
        }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 p-2">
          {/* BRAND SECTION */}
          <div className="d-flex align-items-center gap-2">
            <div style={{ width: "4px", height: "30px", background: `linear-gradient(180deg, ${COLORS.gold} 0%, ${COLORS.goldenrod} 100%)`, borderRadius: "2px", marginRight: 5 }} />
            <div>
              <h6 className="mb-0 text-uppercase" style={{ color: COLORS.gold, letterSpacing: "0.5px", fontSize: "1.1rem", fontWeight: "800", fontFamily: "'Inter', sans-serif" }}>
                {propertyName} <span style={{ color: COLORS.vanilla }}>Dashboard</span>
              </h6>
              <p className="mb-0 text-muted" style={{ color: TEXT_SECONDARY, fontSize: "11px", fontWeight: "600", letterSpacing: "0.2px" }}>⚡ Operations Overview | Code: {hotelCode || "N/A"}</p>
            </div>
          </div>

          {/* ACTIONS SECTION */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {[
              { text: "Reserve Room", link: "/reservation", icon: "➕" },
              { text: "Reservations", link: "/view-reservations", icon: "📅" },
              { text: "Checkouts", link: "/checkouts", icon: "🚪" },
              { text: "Pending", link: "/pending-reservations", icon: "⏳", badge: true },
              { text: "Self Report", link: "/self-report", icon: "📊" },
              { text: "Alerts", link: "/checkout-alert", icon: "⚠️" }
            ].map((item, index) => (
              <button key={index} onClick={() => navigate(item.link)}
                className="btn d-flex flex-column align-items-center justify-content-center position-relative"
                style={{
                  width: "80px", height: "64px", borderRadius: "10px", padding: "6px 2px",
                  background: COLORS.darkGreen, border: `1px solid ${COLORS.calPolyGreen}`,
                  transition: "all 0.2s ease-in-out", boxShadow: "0 2px 4px rgba(0,0,0,0.2)", marginLeft: 5
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.borderColor = COLORS.gold;
                  e.currentTarget.style.background = COLORS.calPolyGreen;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = COLORS.calPolyGreen;
                  e.currentTarget.style.background = COLORS.darkGreen;
                }}
              >
                <span style={{ fontSize: "16px", marginBottom: "4px", lineHeight: "1" }}>{item.icon}</span>
                <span className="text-center font-medium" style={{ color: COLORS.vanilla, fontSize: "9px", fontWeight: "600", lineHeight: "1.1" }}>
                  {item.text}
                </span>
                {item.badge && (
                  <span className="position-absolute rounded-circle" style={{ top: "6px", right: "6px", width: "6px", height: "6px", background: COLORS.gold, boxShadow: `0 0 0 2px ${COLORS.darkGreen}` }} />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="row">
          <div className="col-sm-9">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-2" style={{ background: COLORS.darkGreen, border: `1px solid ${COLORS.calPolyGreen}`, padding: "10px 14px", borderRadius: 16 }}>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* DATE SELECTOR */}
                <div style={{ background: COLORS.bgDark, padding: "8px 14px", borderRadius: 12, border: `1px solid ${COLORS.calPolyGreen}`, display: "flex", alignItems: "center", gap: 12, minWidth: 240 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.darkGreen, flexShrink: 0 }}>
                    <i className="fas fa-calendar-alt" style={{ fontSize: "14px" }}></i>
                  </div>
                  <div style={{ width: "100%" }}>
                    <p style={{ margin: 0, fontSize: "10px", color: TEXT_SECONDARY, fontWeight: 700, textTransform: "uppercase", marginBottom: 1 }}>Dashboard Date</p>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ border: "none", outline: "none", fontWeight: 700, color: COLORS.gold, background: "transparent", fontSize: "14px", width: "100%" }} />
                  </div>
                </div>
              </div>

              {/* VIEW TOGGLE BUTTONS */}
              <div className="d-flex align-items-center gap-1" style={{ background: COLORS.bgDark, padding: 4, borderRadius: 14, border: `1px solid ${COLORS.calPolyGreen}` }}>
                <button onClick={() => setDashboardView("reservations")} style={{ border: "none", marginTop: 0, padding: "8px 20px", borderRadius: 10, fontWeight: 700, fontSize: "13px", transition: "0.2s ease", minWidth: 130, background: dashboardView === "reservations" ? `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})` : "transparent", color: dashboardView === "reservations" ? COLORS.darkGreen : COLORS.vanilla }}>
                  <i className="fas fa-bed me-2 mr-1"></i>Reservations
                </button>
                <button onClick={() => setDashboardView("performance")} style={{ border: "none", marginTop: 0, padding: "8px 20px", borderRadius: 10, fontWeight: 700, fontSize: "13px", transition: "0.2s ease", minWidth: 130, background: dashboardView === "performance" ? `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})` : "transparent", color: dashboardView === "performance" ? COLORS.darkGreen : COLORS.vanilla }}>
                  <i className="fas fa-chart-line me-2 mr-1"></i>Performance
                </button>
              </div>
            </div>

            {dashboardView === "reservations" && (
              <>
                <div className="row p-2">
                  {SUMMARY_CARDS.map((c, i) => (
                    <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-6 mb-2" key={i}>
                      <div onClick={() => navigate(c.link)}
                        style={{
                          background: `linear-gradient(135deg, ${COLORS.darkGreen}, ${COLORS.calPolyGreen})`,
                          borderRadius: "14px", padding: "12px", color: COLORS.vanilla, cursor: "pointer", position: "relative", overflow: "hidden", minHeight: "92px", transition: "all 0.25s ease-in-out", border: `1px solid ${COLORS.calPolyGreen}`, boxShadow: "0 4px 6px rgba(0,0,0,0.3)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-3px)";
                          e.currentTarget.style.borderColor = COLORS.gold;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0px)";
                          e.currentTarget.style.borderColor = COLORS.calPolyGreen;
                        }}
                      >
                        <i className={`fas ${c.icon}`} style={{ position: "absolute", right: "-4px", bottom: "-4px", fontSize: "42px", color: c.color, opacity: 0.15 }} />

                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className={`fas ${c.icon}`} style={{ fontSize: "13px", color: c.color }} />
                          </div>
                        </div>

                        <div style={{ marginTop: "10px" }}>
                          <h5 style={{ margin: 0, fontWeight: "800", fontSize: "18px", color: c.color, letterSpacing: "-0.3px", lineHeight: "1.2" }}>
                            {c.value}
                          </h5>
                          <div style={{ fontSize: "9px", opacity: 0.85, marginTop: "2px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.2px" }}>
                            {c.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* ADVANCED HOTEL OPERATIONS SECTION */}
                <div className="row g-3" style={{ fontFamily: "'Inter', sans-serif" }}>

                  {/* Arrivals for Selected Date */}
                  <div className="mb-2 col-xl-4 col-md-6">
                    <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen, height: "100%" }}>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.calPolyGreen}`, background: `linear-gradient(135deg, ${COLORS.bgDark} 0%, ${COLORS.darkGreen} 100%)` }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 style={{ margin: 0, fontWeight: 700, color: COLORS.gold, fontSize: "15px" }}>Arrivals ({selectedDate})</h6>
                            <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: TEXT_SECONDARY }}>Check-in schedule for date</p>
                          </div>
                          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-door-open" style={{ color: COLORS.vanilla, fontSize: "14px" }} />
                          </div>
                        </div>
                      </div>

                      <div style={{ padding: "12px 20px", maxHeight: "280px", overflowY: "auto" }}>
                        {arrivalsList.length === 0 ? (
                          <p style={{ fontSize: "12px", color: TEXT_SECONDARY, textAlign: "center", padding: "20px 0" }}>No arrivals scheduled for this date.</p>
                        ) : (
                          arrivalsList.map((item, index, arr) => (
                            <div key={index} className="d-flex justify-content-between align-items-center" style={{ padding: "12px 0", borderBottom: index !== arr.length - 1 ? `1px dashed ${COLORS.calPolyGreen}` : "none" }}>
                              <div>
                                <h6 style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: COLORS.vanilla }}>{item.guest}</h6>
                                <span style={{ fontSize: "11px", color: TEXT_SECONDARY, fontWeight: "500" }}>Room {item.room} • <span style={{ color: COLORS.gold }}>{item.source}</span></span>
                              </div>
                              <span style={{ background: COLORS.bgDark, color: COLORS.goldenrod, fontSize: "11px", padding: "4px 10px", borderRadius: "30px", fontWeight: "600", border: `1px solid ${COLORS.calPolyGreen}` }}>
                                {item.time}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* Housekeeping Status for Selected Date */}
                  <div className="mb-2 col-xl-4 col-md-6">
                    <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen, height: "100%" }}>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.calPolyGreen}`, background: `linear-gradient(135deg, ${COLORS.bgDark} 0%, ${COLORS.darkGreen} 100%)` }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 style={{ margin: 0, fontWeight: 700, color: COLORS.gold, fontSize: "15px" }}>Housekeeping Status</h6>
                            <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: TEXT_SECONDARY }}>Availability on {selectedDate}</p>
                          </div>
                          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-broom" style={{ color: COLORS.vanilla, fontSize: "14px" }} />
                          </div>
                        </div>
                      </div>

                      <div style={{ padding: "20px" }}>
                        {[
                          { title: "Available / Clean", value: summary.available, total: summary.totalRooms, color: COLORS.gold },
                          { title: "Dirty / Cleaning", value: summary.cleaning, total: summary.totalRooms, color: COLORS.goldenrod },
                          { title: "Occupied / Booked", value: summary.live, total: summary.totalRooms, color: COLORS.vanilla }
                        ].map((item, index) => (
                          <div key={index} style={{ marginBottom: "14px" }}>
                            <div className="d-flex justify-content-between mb-1.5">
                              <span style={{ fontSize: "12px", fontWeight: "600", color: COLORS.vanilla }}>{item.title}</span>
                              <span style={{ fontSize: "12px", color: COLORS.gold, fontWeight: "700" }}>{item.value} <span style={{ color: TEXT_SECONDARY, fontWeight: "400" }}>/ {item.total || 0}</span></span>
                            </div>
                            <div style={{ height: "6px", borderRadius: "30px", background: COLORS.bgDark, overflow: "hidden", border: `1px solid ${COLORS.calPolyGreen}` }}>
                              <div style={{ width: `${item.total > 0 ? (item.value / item.total) * 100 : 0}%`, height: "100%", background: item.color, borderRadius: "30px" }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>

                  {/* Room Block Management */}
                  <div className="mb-2 col-xl-4 col-md-12">
                    <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen, height: "100%" }}>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.calPolyGreen}`, background: `linear-gradient(135deg, ${COLORS.bgDark} 0%, ${COLORS.darkGreen} 100%)` }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 style={{ margin: 0, fontWeight: 700, color: COLORS.gold, fontSize: "15px" }}>Room Block Management</h6>
                            <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: TEXT_SECONDARY }}>Hold rooms for maintenance</p>
                          </div>
                          <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-ban" style={{ color: COLORS.vanilla, fontSize: "14px" }} />
                          </div>
                        </div>
                      </div>

                      <div style={{ padding: "16px 20px", marginBottom: 10 }}>
                        <div className="row g-2">
                          <div className="mb-2 col-12">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Reason (e.g., AC Repair)"
                              value={blockReason}
                              onChange={(e) => setBlockReason(e.target.value)}
                              style={{ borderRadius: "8px", border: `1px solid ${COLORS.calPolyGreen}`, padding: "8px 12px", background: COLORS.bgDark, color: COLORS.vanilla }}
                            />
                          </div>
                          <div className="mb-2 col-6">
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={blockStartDate}
                              onChange={(e) => setBlockStartDate(e.target.value)}
                              style={{ borderRadius: "8px", border: `1px solid ${COLORS.calPolyGreen}`, fontSize: "12px", padding: "6px 10px", background: COLORS.bgDark, color: COLORS.vanilla }}
                            />
                          </div>
                          <div className="mb-2 col-6">
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={blockEndDate}
                              onChange={(e) => setBlockEndDate(e.target.value)}
                              style={{ borderRadius: "8px", border: `1px solid ${COLORS.calPolyGreen}`, fontSize: "12px", padding: "6px 10px", background: COLORS.bgDark, color: COLORS.vanilla }}
                            />
                          </div>
                          <div className="col-12">
                            <select
                              className="form-select form-select-sm"
                              value={blockSelectedRoomId}
                              onChange={(e) => setBlockSelectedRoomId(e.target.value)}
                              style={{ borderRadius: "8px", border: `1px solid ${COLORS.calPolyGreen}`, padding: "8px 12px", background: COLORS.bgDark, color: COLORS.vanilla }}
                            >
                              <option value="">Select Room (All Rooms)...</option>
                              {rawRooms.map((r) => (
                                <option key={r.id} value={r.id}>
                                  Room {r.roomNo} - {r.roomTypeName || r.category || "Standard"}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <button
                          onClick={handleBlockRoomSubmit}
                          disabled={isBlockingSubmitting}
                          className="btn w-100 btn-sm transition-all mt-2"
                          style={{ background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`, color: COLORS.darkGreen, borderRadius: "10px", padding: "8px", fontWeight: "800", border: "none" }}
                        >
                          {isBlockingSubmitting ? "Blocking Room..." : "Block Unit"}
                        </button>
                      </div>
                    </Card>
                  </div>

                  {/* Blocked Room Revenue Loss */}
                  <div className="mb-2 col-xl-6 col-md-12">
                    <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen, height: "100%" }}>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.calPolyGreen}` }}>
                        <h6 style={{ margin: 0, fontWeight: 700, color: COLORS.gold, fontSize: "15px" }}>Blocked Room Revenue Loss ({selectedDate})</h6>
                      </div>
                      <div style={{ padding: "16px 20px", marginBottom: 10, maxHeight: "200px", overflowY: "auto" }}>
                        {blockedLossList.length === 0 ? (
                          <p style={{ fontSize: "12px", color: TEXT_SECONDARY, textAlign: "center", padding: "10px 0" }}>No rooms blocked for this date.</p>
                        ) : (
                          <div className="row g-2">
                            {blockedLossList.map((item, index) => (
                              <div className="col-6" key={index}>
                                <div style={{ background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}`, borderRadius: "12px", padding: "12px" }}>
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <h6 style={{ margin: 0, fontWeight: "700", fontSize: "13px", color: COLORS.vanilla }}>Room {item.room}</h6>
                                      <span style={{ fontSize: "11px", color: TEXT_SECONDARY }}>{item.reason} • {item.days}d</span>
                                    </div>
                                    <h6 style={{ margin: 0, color: COLORS.goldenrod, fontWeight: "700", fontSize: "13px" }}>₹{item.loss}</h6>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>

                  {/* Pending Operational Tasks */}
                  <div className="mb-2 col-xl-6 col-md-12">
                    <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen, height: "100%" }}>
                      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.calPolyGreen}` }}>
                        <h6 style={{ margin: 0, fontWeight: 700, color: COLORS.gold, fontSize: "15px" }}>Pending Operational Tasks</h6>
                      </div>
                      <div style={{ padding: "16px 20px", marginBottom: 10, maxHeight: "200px", overflowY: "auto" }}>
                        <div className="row g-2">
                          {operationalTasks.map((item, index) => (
                            <div className="col-6" key={index}>
                              <div style={{ background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}`, borderRadius: "12px", padding: "12px", opacity: item.solved ? 0.6 : 1 }}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 style={{ margin: 0, fontWeight: "600", fontSize: "13px", color: COLORS.vanilla, textDecoration: item.solved ? "line-through" : "none" }}>{item.concern}</h6>
                                    <span style={{ fontSize: "11px", color: TEXT_SECONDARY }}>Room {item.room}</span>
                                  </div>
                                  <span
                                    onClick={() => handleToggleTask(item)}
                                    style={{ cursor: "pointer", background: item.solved ? COLORS.calPolyGreen : COLORS.darkGreen, color: COLORS.gold, padding: "3px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "700", border: `1px solid ${COLORS.gold}` }}
                                  >
                                    {item.solved ? "Done" : "Pending"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* OTA BOOKING LOGS & SYNC ENGINE */}
                <div className="row g-2 mb-3 p-2">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 w-100">
                    <div className="d-flex align-items-center gap-2">
                      <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <i className="fas fa-globe" style={{ color: COLORS.gold, fontSize: "14px" }} />
                      </div>
                      <div>
                        <h6 className="mb-0" style={{ color: COLORS.gold, fontWeight: 800, textTransform: "uppercase", fontSize: "0.95rem", letterSpacing: "0.5px" }}>
                          OTA Booking Logs & Sync Engine ({selectedDate})
                        </h6>
                        <span style={{ color: TEXT_SECONDARY, fontSize: "11px" }}>Total fetched: {filteredBookings.length} bookings</span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <div style={{ position: "relative" }}>
                        <i className="fas fa-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: TEXT_SECONDARY, fontSize: "11px" }} />
                        <input
                          type="text"
                          placeholder="Search guest, channel, ID..."
                          value={searchText}
                          onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                          style={{
                            padding: '6px 12px 6px 30px',
                            borderRadius: '10px',
                            border: `1px solid ${COLORS.calPolyGreen}`,
                            background: COLORS.bgDark,
                            color: COLORS.vanilla,
                            outline: 'none',
                            fontSize: "12px",
                            minWidth: '240px'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    {currentRows.length === 0 ? (
                      <div className="col-12 text-center py-5" style={{ background: COLORS.darkGreen, borderRadius: "14px", border: `1px dashed ${COLORS.calPolyGreen}`, color: TEXT_SECONDARY }}>
                        <i className="fas fa-inbox fa-2x mb-2" style={{ color: COLORS.calPolyGreen }} />
                        <p style={{ margin: 0, fontSize: "13px" }}>No OTA booking records match your filter for this date.</p>
                      </div>
                    ) : (
                      currentRows.map((row) => {
                        const resDetails = row.payload?.HotelReservations?.HotelReservation;
                        const customer = resDetails?.ResGuests?.ResGuest?.Profiles?.ProfileInfo?.Profile?.Customer;
                        const guestName = `${customer?.PersonName?.GivenName || ""} ${customer?.PersonName?.Surname || ""}`.trim() || "Guest";
                        const mobile = customer?.Telephone?.[0]?.PhoneNumber || "N/A";
                        const otaId = resDetails?.UniqueID?.ID || row.id;
                        const roomStay = resDetails?.RoomStays?.[0];
                        const checkin = roomStay?.TimeSpan?.Start ? moment(roomStay.TimeSpan.Start).format("DD MMM, YYYY") : "N/A";
                        const checkout = roomStay?.TimeSpan?.End ? moment(roomStay.TimeSpan.End).format("DD MMM, YYYY") : "N/A";
                        const amount = Number(roomStay?.Total?.AmountAfterTax || roomStay?.Total?.AmountBeforeTax || 0);
                        const roomName = roomStay?.RoomTypes?.RoomType?.RoomDescription?.Name || "Standard Room";
                        const channelName = row.payload?.POS?.Source?.BookingChannel?.CompanyName || "Direct OTA";

                        return (
                          <div className="col-12 col-md-6 col-xl-4" key={row.id}>
                            <div
                              style={{
                                background: COLORS.darkGreen,
                                borderRadius: "14px",
                                border: `1px solid ${COLORS.calPolyGreen}`,
                                borderLeft: `5px solid ${row.isTransferred ? COLORS.calPolyGreen : COLORS.gold}`,
                                boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                                padding: "16px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                transition: "transform 0.2s ease"
                              }}
                            >
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-2 pb-2" style={{ borderBottom: `1px solid rgba(21, 83, 50, 0.4)` }}>
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ background: COLORS.bgDark, color: COLORS.gold, border: `1px solid ${COLORS.calPolyGreen}`, padding: "3px 8px", borderRadius: "6px", fontSize: "10px", fontWeight: "700", textTransform: "uppercase" }}>
                                      {channelName}
                                    </span>
                                    <span style={{ fontSize: "11px", color: TEXT_SECONDARY, fontWeight: "600" }}>#{otaId}</span>
                                  </div>
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: "800",
                                      textTransform: "uppercase",
                                      padding: "3px 8px",
                                      borderRadius: "12px",
                                      background: row.isTransferred ? "rgba(21, 83, 50, 0.5)" : "rgba(255, 213, 0, 0.15)",
                                      color: row.isTransferred ? COLORS.vanilla : COLORS.gold,
                                      border: `1px solid ${row.isTransferred ? COLORS.calPolyGreen : COLORS.gold}`
                                    }}
                                  >
                                    ● {row.isTransferred ? "Synced to PMS" : "Pending Action"}
                                  </span>
                                </div>

                                <div className="mb-2">
                                  <h6 className="mb-0 text-truncate" style={{ fontSize: "14px", fontWeight: "700", color: COLORS.vanilla }} title={roomName}>
                                    {roomName}
                                  </h6>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    <i className="fas fa-user text-muted" style={{ fontSize: "10px", color: TEXT_SECONDARY }} />
                                    <span style={{ fontSize: "12px", fontWeight: "600", color: COLORS.vanilla }}>{guestName}</span>
                                    <span style={{ fontSize: "11px", color: TEXT_SECONDARY }}>({mobile})</span>
                                  </div>
                                </div>

                                <div className="p-2 mb-3 d-flex justify-content-between align-items-center text-center" style={{ background: COLORS.bgDark, borderRadius: "8px", border: `1px solid ${COLORS.calPolyGreen}` }}>
                                  <div>
                                    <div style={{ fontSize: "9px", color: TEXT_SECONDARY, fontWeight: "700", textTransform: "uppercase" }}>Check-In</div>
                                    <div style={{ fontSize: "11px", fontWeight: "700", color: COLORS.gold }}>{checkin}</div>
                                  </div>
                                  <i className="fas fa-arrow-right" style={{ color: COLORS.calPolyGreen, fontSize: "11px" }} />
                                  <div>
                                    <div style={{ fontSize: "9px", color: TEXT_SECONDARY, fontWeight: "700", textTransform: "uppercase" }}>Check-Out</div>
                                    <div style={{ fontSize: "11px", fontWeight: "700", color: COLORS.goldenrod }}>{checkout}</div>
                                  </div>
                                </div>
                              </div>

                              <div className="pt-2" style={{ borderTop: `1px dashed ${COLORS.calPolyGreen}` }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span style={{ fontSize: "11px", color: TEXT_SECONDARY, fontWeight: "600" }}>Total Booking Value:</span>
                                  <span style={{ fontSize: "15px", fontWeight: "800", color: COLORS.gold }}>₹{amount.toLocaleString("en-IN")}</span>
                                </div>

                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => handleViewDetails(row)}
                                    className="btn btn-sm w-50"
                                    style={{
                                      background: COLORS.bgDark,
                                      color: COLORS.vanilla,
                                      border: `1px solid ${COLORS.calPolyGreen}`,
                                      fontSize: "11px",
                                      fontWeight: "700",
                                      borderRadius: "8px"
                                    }}
                                  >
                                    <i className="fas fa-eye me-1" /> Payload
                                  </button>
                                  <button
                                    onClick={() => processReservationTransfer(row)}
                                    disabled={row.isTransferred}
                                    className="btn btn-sm w-50"
                                    style={{
                                      background: row.isTransferred ? COLORS.bgDark : `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                                      color: row.isTransferred ? TEXT_SECONDARY : COLORS.darkGreen,
                                      border: `1px solid ${row.isTransferred ? COLORS.calPolyGreen : COLORS.gold}`,
                                      fontSize: "11px",
                                      fontWeight: "800",
                                      borderRadius: "8px"
                                    }}
                                  >
                                    <i className={`fas ${row.isTransferred ? "fa-check-circle" : "fa-exchange-alt"} me-1`} />
                                    {row.isTransferred ? "Transferred" : "Transfer Unit"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center w-100 mt-3 pt-2" style={{ borderTop: `1px solid ${COLORS.calPolyGreen}` }}>
                      <span style={{ color: TEXT_SECONDARY, fontSize: '12px' }}>
                        Showing Page {currentPage} of {totalPages}
                      </span>
                      <div className="d-flex gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                          disabled={currentPage === 1}
                          className="btn btn-sm"
                          style={{ background: COLORS.darkGreen, border: `1px solid ${COLORS.calPolyGreen}`, color: COLORS.vanilla, fontSize: "11px", borderRadius: "6px" }}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="btn btn-sm"
                          style={{ background: COLORS.darkGreen, border: `1px solid ${COLORS.calPolyGreen}`, color: COLORS.vanilla, fontSize: "11px", borderRadius: "6px" }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* PERFORMANCE VIEW */}
            {dashboardView === "performance" && (
              <>
                <div className="row g-3 p-2">
                  <div className="col-xl-3 col-md-6">
                    <div style={{ background: COLORS.darkGreen, borderRadius: "16px", padding: "16px 20px", position: "relative", overflow: "hidden", border: `1px solid ${COLORS.calPolyGreen}`, minHeight: "115px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "5px", background: `linear-gradient(180deg, ${COLORS.gold}, ${COLORS.goldenrod})` }} />
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span style={{ color: TEXT_SECONDARY, fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>Occupancy ({selectedDate})</span>
                          <h3 style={{ margin: "4px 0 0 0", fontWeight: "800", fontSize: "1.65rem", color: COLORS.vanilla }}>
                            {summary.totalRooms > 0 ? ((summary.live / summary.totalRooms) * 100).toFixed(1) : 0}%
                          </h3>
                        </div>
                        <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-hotel" style={{ color: COLORS.gold, fontSize: "14px" }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <div className="revenue-card-clickable" data-bs-toggle="collapse" data-bs-target="#revenueBreakdown" style={{ background: COLORS.darkGreen, borderRadius: "16px", padding: "16px 20px", position: "relative", overflow: "hidden", border: `1px solid ${COLORS.calPolyGreen}`, minHeight: "115px", cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "5px", background: `linear-gradient(180deg, ${COLORS.calPolyGreen}, ${COLORS.vanilla})` }} />
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span style={{ color: TEXT_SECONDARY, fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>Month Revenue</span>
                          <h3 style={{ margin: "4px 0 0 0", fontWeight: "800", fontSize: "1.65rem", color: COLORS.vanilla }}>₹{summary.monthRevenue.toLocaleString("en-IN")}</h3>
                        </div>
                        <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.bgDark, display: "flex", alignItems: "center", justifyContent: "center", border: `1px solid ${COLORS.calPolyGreen}` }}>
                          <i className="fas fa-indian-rupee-sign" style={{ color: COLORS.gold, fontSize: "14px" }} />
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-between" style={{ fontSize: "11px" }}>
                        <span style={{ color: COLORS.gold, fontWeight: "700" }}>₹{Number(summary.todayRevenue).toLocaleString("en-IN")} for selected date</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <div style={{ background: COLORS.darkGreen, borderRadius: "16px", padding: "16px 20px", position: "relative", overflow: "hidden", border: `1px solid ${COLORS.calPolyGreen}`, minHeight: "115px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "5px", background: `linear-gradient(180deg, ${COLORS.goldenrod}, ${COLORS.gold})` }} />
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span style={{ color: TEXT_SECONDARY, fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>Pickup ({selectedDate})</span>
                          <h3 style={{ margin: "4px 0 0 0", fontWeight: "800", fontSize: "1.65rem", color: COLORS.vanilla }}>
                            {summary.today || 0} <span style={{ fontSize: "13px", fontWeight: "600", color: TEXT_SECONDARY }}>rooms</span>
                          </h3>
                        </div>
                        <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-bed" style={{ color: COLORS.gold, fontSize: "14px" }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <div style={{ background: COLORS.darkGreen, borderRadius: "16px", padding: "16px 20px", position: "relative", overflow: "hidden", border: `1px solid ${COLORS.calPolyGreen}`, minHeight: "115px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                      <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "5px", background: `linear-gradient(180deg, ${COLORS.vanilla}, ${COLORS.calPolyGreen})` }} />
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <span style={{ color: TEXT_SECONDARY, fontSize: "11px", fontWeight: "700", textTransform: "uppercase" }}>Pre-Booking</span>
                          <h3 style={{ margin: "4px 0 0 0", fontWeight: "800", fontSize: "1.65rem", color: COLORS.vanilla }}>
                            {preBookingWindowDays} <span style={{ fontSize: "13px", fontWeight: "600", color: TEXT_SECONDARY }}>days</span>
                          </h3>
                        </div>
                        <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-calendar-alt" style={{ color: COLORS.gold, fontSize: "14px" }} />
                        </div>
                      </div>
                      <div style={{ fontSize: "11px", fontWeight: "600", color: TEXT_SECONDARY }}>Average lead for month</div>
                    </div>
                  </div>
                </div>

                <div className="collapse show p-2" id="revenueBreakdown">
                  <div className="row">
                    <div className="col-lg-6 mb-2">
                      <Card style={{ padding: "14px", borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div>
                            <h5 style={{ margin: 0, fontWeight: "800", color: COLORS.gold, fontSize: "1.1rem" }}>Revenue Generated By Sources</h5>
                            <p style={{ margin: "4px 0 0 0", color: TEXT_SECONDARY, fontSize: "13px" }}>Month: {moment(selectedDate).format("MMMM YYYY")}</p>
                          </div>
                          <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-chart-pie" style={{ color: COLORS.gold, fontSize: "16px" }} />
                          </div>
                        </div>

                        {sourceRevenueData.length === 0 ? (
                          <p style={{ color: TEXT_SECONDARY, textAlign: "center", padding: "20px 0" }}>No source revenue recorded yet for this month.</p>
                        ) : (
                          sourceRevenueData.map((item, index, arr) => (
                            <div key={index} className={index !== arr.length - 1 ? "mb-3" : ""} style={{ background: COLORS.bgDark, borderRadius: "12px", padding: "14px 16px", border: `1px solid ${COLORS.calPolyGreen}` }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 style={{ margin: 0, fontWeight: "700", color: COLORS.vanilla, fontSize: "14px" }}>{item.source}</h6>
                                  <div style={{ color: TEXT_SECONDARY, fontSize: "12px", marginTop: "2px" }}>{item.rooms} Rooms Sold</div>
                                </div>
                                <div className="text-end">
                                  <h6 style={{ margin: 0, color: item.color, fontWeight: "800", fontSize: "15px" }}>₹{item.revenue.toLocaleString("en-IN")}</h6>
                                </div>
                              </div>
                              <div style={{ marginTop: "10px", height: "6px", background: COLORS.darkGreen, borderRadius: "10px", overflow: "hidden" }}>
                                <div style={{ width: `${summary.monthRevenue > 0 ? (item.revenue / summary.monthRevenue) * 100 : 0}%`, height: "100%", background: item.color, borderRadius: "10px" }} />
                              </div>
                            </div>
                          ))
                        )}
                      </Card>
                    </div>

                    <div className="col-lg-6 mb-2">
                      <Card style={{ padding: "14px", borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <div>
                            <h5 style={{ margin: 0, fontWeight: "800", color: COLORS.gold, fontSize: "1.1rem" }}>Revenue By Room Type</h5>
                            <p style={{ margin: "4px 0 0 0", color: TEXT_SECONDARY, fontSize: "13px" }}>Month: {moment(selectedDate).format("MMMM YYYY")}</p>
                          </div>
                          <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-door-open" style={{ color: COLORS.gold, fontSize: "16px" }} />
                          </div>
                        </div>

                        {roomTypeRevenueData.length === 0 ? (
                          <p style={{ color: TEXT_SECONDARY, textAlign: "center", padding: "20px 0" }}>No category revenue recorded for this month.</p>
                        ) : (
                          roomTypeRevenueData.map((item, index, arr) => (
                            <div key={index} className={index !== arr.length - 1 ? "mb-3" : ""} style={{ padding: "14px 16px", borderRadius: "12px", background: COLORS.bgDark, border: `1px solid ${COLORS.calPolyGreen}` }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 style={{ margin: 0, fontWeight: "700", color: COLORS.vanilla, fontSize: "14px" }}>{item.room}</h6>
                                  <div style={{ marginTop: "2px", color: TEXT_SECONDARY, fontSize: "12px" }}>Rooms: {item.roomNos}</div>
                                </div>
                                <div>
                                  <h6 style={{ margin: 0, fontWeight: "800", color: COLORS.gold, fontSize: "15px" }}>₹{item.revenue.toLocaleString("en-IN")}</h6>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </Card>
                    </div>

                    <div className="col-lg-12 mb-2">
                      <Card style={{ padding: "24px", borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, background: COLORS.darkGreen }}>
                        <div className="mb-4">
                          <h5 style={{ margin: 0, fontWeight: "800", color: COLORS.gold, fontSize: "1.1rem" }}>Room Pickup By Source</h5>
                          <p style={{ margin: "4px 0 0 0", color: TEXT_SECONDARY, fontSize: "13px" }}>Live pickup distribution for {moment(selectedDate).format("MMMM YYYY")}</p>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={dynamicBarData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <XAxis dataKey="name" tick={{ fill: COLORS.vanilla, fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: COLORS.vanilla, fontSize: 12 }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: COLORS.bgDark }} contentStyle={{ backgroundColor: COLORS.darkGreen, borderColor: COLORS.calPolyGreen, color: COLORS.vanilla }} />
                            <Bar dataKey="rooms" fill={COLORS.gold} radius={[6, 6, 0, 0]} maxBarSize={45} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="col-sm-3">
            {/* UPDATED HOTEL WALLET CARD */}
            <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, overflow: "hidden", marginBottom: "16px", background: `linear-gradient(135deg, ${COLORS.darkGreen} 0%, ${COLORS.bgDark} 100%)`, boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
              <div style={{ padding: "20px" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p style={{ margin: 0, color: TEXT_SECONDARY, fontWeight: "700", fontSize: "10px", letterSpacing: "1px" }}>HOTEL WALLET</p>
                    <h3 style={{ color: COLORS.gold, fontWeight: "800", marginTop: "4px", marginBottom: 0, fontSize: "24px" }}>
                      ₹{walletBalance.toLocaleString("en-IN")}
                    </h3>
                    <span style={{ fontSize: "11px", color: TEXT_SECONDARY }}>Available Balance</span>
                  </div>
                  <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-wallet" style={{ color: COLORS.gold, fontSize: "18px" }} />
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                  <button
                    onClick={handleOpenAddMoneyModal}
                    style={{
                      flex: 1,
                      border: "none",
                      borderRadius: "10px",
                      padding: "10px 0",
                      background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                      color: COLORS.darkGreen,
                      fontWeight: "800",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <i className="fas fa-plus-circle" /> Add Money
                  </button>
                  <button
                    onClick={() => navigate(`/wallet-history/${propertyId}`)}
                    style={{
                      flex: 1,
                      border: `1px solid ${COLORS.gold}`,
                      borderRadius: "10px",
                      padding: "10px 0",
                      background: "transparent",
                      color: COLORS.gold,
                      fontWeight: "700",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px"
                    }}
                  >
                    <i className="fas fa-receipt" /> History
                  </button>
                </div>
              </div>
            </Card>

            {/* MARKETING CARD */}
            <Card style={{ borderRadius: "16px", border: `1px solid ${COLORS.calPolyGreen}`, overflow: "hidden", marginBottom: "16px", position: "relative", background: `linear-gradient(135deg, ${COLORS.bgDark} 0%, ${COLORS.darkGreen} 100%)`, boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
              <div style={{ position: "absolute", width: "140px", height: "140px", borderRadius: "50%", background: "rgba(255,255,255,0.03)", top: "-40px", right: "-40px" }} />
              <div style={{ padding: "20px", position: "relative", zIndex: 1 }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: COLORS.calPolyGreen, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fas fa-bullhorn" style={{ color: COLORS.gold, fontSize: "16px" }} />
                  </div>
                  <span style={{ background: COLORS.darkGreen, color: COLORS.gold, border: `1px solid ${COLORS.calPolyGreen}`, padding: "4px 10px", borderRadius: "20px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px" }}>
                    MARKETING
                  </span>
                </div>
                <div style={{ marginTop: "16px" }}>
                  <h6 style={{ color: COLORS.vanilla, fontWeight: "800", marginBottom: "6px", fontSize: "15px" }}>Upgrade To Boost 🚀</h6>
                  <p style={{ color: TEXT_SECONDARY, lineHeight: 1.5, fontSize: "12px", margin: 0 }}>Increase visibility, receive premium leads, WhatsApp alerts, and priority admin listing.</p>
                </div>
                <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  {["Featured Hotel Listing", "Premium WhatsApp Leads", "Boost OTA Visibility"].map((feature, idx) => (
                    <div key={idx} className="d-flex align-items-center">
                      <div style={{ width: "18px", height: "18px", borderRadius: "50%", background: COLORS.darkGreen, display: "flex", alignItems: "center", justifyContent: "center", marginRight: "8px", border: `1px solid ${COLORS.calPolyGreen}` }}>
                        <i className="fas fa-check" style={{ color: COLORS.gold, fontSize: "9px" }} />
                      </div>
                      <span style={{ color: COLORS.vanilla, fontSize: "12px", fontWeight: "500" }}>{feature}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate("/marketing-plans")} style={{ width: "100%", marginTop: "16px", border: "none", borderRadius: "12px", padding: "10px", fontWeight: "700", fontSize: "13px", color: COLORS.darkGreen, background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  <i className="fas fa-rocket" style={{ fontSize: "12px" }}></i> Upgrade Now
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;